import React, { useEffect, useMemo, useState, useCallback } from "react";

import { formatWithDecimals, isNonEmptyArray, shallowCopy } from "_helpers";

import { ReactComponent as Attachment } from "_assets/folderDetail/invoices/messages/attachment.svg";
import { ReactComponent as Send } from "_assets/folderDetail/invoices/messages/send.svg";

import { Button, Col, message, Progress, Row, Space, Tag, Upload } from "antd";

import styles from "_styles/Components/_Textarea.module.css";

export const CustomTextarea = ({ name, textareaRef, textareaHandler, textareaHeight, pj, sendButton, onSubmitMessage }) => {
    const { values, setValues, initialValues } = textareaHandler;

    const [ idToRemove, setIdToRemove ] = useState();
    const [ totalSizeToUpload, setTotalSizeToUpload ] = useState(0);
    const [ totalProgressParams, setTotalProgressParams ] = useState({ percent: 0, status: undefined });

    const [ messageApi, contextHolder ] = message.useMessage();

    const typesAllowed = [
        'image/png',
        'image/jpg',
        'image/jpeg',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'application/pdf'
    ];
    const maxSizeAllowed = 2;

    useEffect(() => {
        if( !pj ){
            setValues(initialValues);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pj])

    useEffect(() => {
        let status;

        if( totalSizeToUpload > maxSizeAllowed ){
            status = 'exception';
        } else if( totalSizeToUpload < maxSizeAllowed ){
            status = 'active';
        } else if( totalSizeToUpload === maxSizeAllowed ){
            status = 'success';
        }

        setTotalProgressParams({
            percent: totalSizeToUpload/maxSizeAllowed * 100,
            status
        })
    }, [totalSizeToUpload, maxSizeAllowed])

    useEffect(() => {
        if( isNonEmptyArray(values.attachments) ){
            const attachments = values.attachments;
    
            const totalSizeInOctets = attachments.reduce((acc, val) => acc + val.size, 0);
            const totalSizeInMegaOctets = formatWithDecimals((totalSizeInOctets / 1000000), 1);
    
            setTotalSizeToUpload(totalSizeInMegaOctets);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.attachments])

    const success = useCallback(( message ) => {
        messageApi.success(message);
    } , [])

    const error = useCallback(( message, duration = 1.5 ) => {
        messageApi.error(message, duration);
    } , [])

    useEffect(() => {
        if( values.attachments ){
            const attachments = values.attachments;
            const found = attachments.find(el => el.uid === idToRemove);
    
            if( idToRemove && found ){
                handleRemovePjItem(idToRemove);
                setIdToRemove(null);
                error('Ce type de pièce n\'est pas pris en charge (uniquement .jpg, .png, .pdf, .xls, .xlsx)', 6);
            }
        }
    }, [values.attachments, idToRemove, error])

    const handleChange = ({ name, value }) => {
        setValues(oldVal => ({ ...oldVal, [name]: value }))
    }

    const handleRemovePjItem = ( id ) => {
        let attachmentsCopy = shallowCopy(values.attachments);
        attachmentsCopy = attachmentsCopy.filter(l => l.uid !== id);

        setValues(oldVal => ({ ...oldVal, attachments: attachmentsCopy }));
    }

    return (
        <>
            { contextHolder }
            <Row gutter={4} className={['mt-2'].join(" ")} style={{ height: textareaHeight, maxHeight: isNonEmptyArray(values.attachments) ? '125px' : '230px' }}>
                <Col flex="auto">
                    <div className={styles.rowTextarea}>
                        <textarea
                            ref={textareaRef}
                            value={values.text}
                            rows={1}
                            name={name ?? 'text'}
                            className={styles.textarea}
                            onChange={({ currentTarget }) => handleChange(currentTarget)}
                        />

                        {
                            pj && 
                                <Upload
                                    accept="image/*, .pdf, .xlsx"
                                    multiple
                                    beforeUpload={({ type, uid }) => {
                                        if( !typesAllowed.includes(type) ) {
                                            setIdToRemove(uid);
                                            return false;
                                        }
                                        return true;
                                    }}
                                    onChange={({ fileList }) => handleChange({ name: 'attachments', value: fileList })}
                                    fileList={values.attachments}
                                    itemRender={() => undefined}
                                >
                                    <Button
                                        type="text"
                                        icon={<Attachment height={24} width={24} />}
                                        className={styles.attachmentBtn}
                                    />
                                </Upload>
                        }
                    </div>
                </Col>
                {
                    sendButton &&
                        <Col xs={2} className="h-100 d-flex">
                            <Button 
                                type="primary" 
                                className={["w-100", textareaHeight > 32 ? "mt-auto" : undefined].join(" ")} 
                                icon={<Send height={24} width={24} />} 
                                onClick={onSubmitMessage}
                            />
                        </Col>
                }
            </Row>
            {
                pj && isNonEmptyArray(values.attachments) &&
                    <Row
                        style={{ height: '110px' }}
                    >
                        <Space 
                            className="mt-2 w-100" 
                            direction="vertical"
                            style={{ maxHeight: '72px', overflow: 'auto' }}
                        >
                            {
                                values.attachments.map((item) => {
                                    const { uid, name, percent, status } = item;

                                    return (
                                        <div className="d-flex align-items-center">
                                            <Progress 
                                                type="circle" 
                                                percent={percent} 
                                                strokeWidth={20}
                                                size={18}
                                                status={() => {
                                                    switch( status ){
                                                        case 'uploading' : return 'active' ;
                                                        case 'done' : return 'success' ;
                                                        case 'error' : return 'exception' ;
                                                        case 'success' :
                                                        case 'removed' :
                                                        default : break ;
                                                    }
                                                }}
                                                format={(p) => p + '%'}
                                                className="me-1"
                                            />
                                            <Tag 
                                                key={uid} 
                                                closable
                                                onClose={() => handleRemovePjItem(uid)}
                                                style={{ width: 'inherit' }}
                                            >
                                                { name }
                                            </Tag>
                                        </div>
                                    )
                                })
                            }
                        </Space>
                        <Progress 
                            percent={totalProgressParams.percent}
                            status={totalProgressParams.status}
                            format={() => totalSizeToUpload + '/' + maxSizeAllowed + ' Mo'}
                            style={{ paddingRight: '35px', marginRight: '0px' }}
                        />
                    </Row>
            }
        </>
    )
}